// @flow

import { createSelector } from 'reselect';

import {
  selectPrepaid,
  createPrepaid,
  replacePrepaid,
  type Currency,
} from '@gandi/billing';
import {
  patchOneOrganization,
  type CreateData,
  organizationsResource,
} from '@gandi/redux-organization';
import { selectCurrencies as selectPlacesCurrencies_ } from '@gandi/redux-places';
import { fetchCurrentSharing, selectCurrentId } from '@gandi/redux-sharing';

import type { FullState, Dispatch, GetState } from 'src/types';

export const patchOrganizationAndPrepaid =
  (
    sharingId: string,
    data: Partial<CreateData>,
    currency: Currency | null,
    dryrun?: boolean,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<any>) =>
  async (dispatch: Dispatch, getState: GetState) =>
    // $FlowFixMe[incompatible-call]
    dispatch(patchOneOrganization(sharingId, data, { dryrun })).then(
      response => {
        if (sharingId === selectCurrentId(getState())) {
          dispatch(fetchCurrentSharing());
        } else {
          dispatch(organizationsResource.invalidate());
        }

        if (!currency || dryrun) {
          return Promise.resolve(response);
        }

        // $FlowFixMe[incompatible-call]
        const hasPrepaid = selectPrepaid(getState(), { sharingId });
        if (!hasPrepaid) {
          return dispatch(
            createPrepaid({ currency, optimistic: true }, { sharingId }),
          );
        }

        return dispatch(replacePrepaid({ currency }, { sharingId }));
      },
    );

type CurrenciesProps = {
  canChangeCurrency: boolean,
  currency: ?Currency,
  country: ?string,
  bu?: ?number,
};

const selectProps = (_: any, props: CurrenciesProps): CurrenciesProps => props;

// Retrieve the list of available currencies from `@gandi/redux-places`
// ** for the last asked country ** (thus bypassing the `alpha2?` props param)
const selectPlacesCurrencies = (
  state: FullState,
  props: CurrenciesProps,
): Array<Currency> =>
  // $FlowFixMe[incompatible-return]
  selectPlacesCurrencies_(
    state,
    // $FlowFixMe - calling selectCurrencies with undefined is valid.
    props.bu ? `${props.country}-${props.bu}` : props.country,
  );

const EMPTY_ARRAY: Array<Currency> = [];
// Add the current prepaid currency to the list of available ones
export const selectCurrencies: (
  state: FullState,
  props: CurrenciesProps,
) => Array<Currency> = createSelector(
  [selectPlacesCurrencies, selectProps],
  (placesCurrencies, props) => {
    let currencies = EMPTY_ARRAY;

    if (props.currency && !props.canChangeCurrency) {
      currencies = [props.currency];
    }

    if (props.canChangeCurrency && placesCurrencies.length) {
      currencies = placesCurrencies;
    }

    return currencies;
  },
);
